import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Box,
  Text,
  Badge,
  Flex,
  Grid,
  Icon,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Button,
  Image,
  Spinner,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import {
  Buildings,
  Calendar,
  Users,
  Tag,
  WarningCircle,
  MapPin,
  IdentificationCard,
  FolderOpen,
  X,
  NotePencil,
  PencilSimple,
  ChartBar,
  Gear,
  Phone,
  Camera,
  Trash,
  Calculator,
} from 'phosphor-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import ViewNotaModal from '../viewNota';
import ContactFormModal from '../WhatsAppChat/ContactFormModal';
import { api } from '../../services/api';
import { ICompany, useCompanies } from '../../hooks/useCompanies';
import { formatCPFOrCNPJ, getDocumentType } from '../../utils/FormatFunctions';
import { EmpresaDetailModal } from '../../pages/empresaDashboard/components';
import { SimplesNacionalTab, useParametroSn } from './SimplesNacionalTab';

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: ICompany | null;
  // eslint-disable-next-line no-unused-vars
  onEditCharacteristics?: (company: ICompany) => void;
  showNotesButton?: boolean;
  showDashboardButton?: boolean;
}

interface InfoItemProps {
  label: string;
  value: any;
}

function InfoItem({ label, value }: InfoItemProps) {
  const displayValue = () => {
    if (value === null || value === undefined || value === '') return '-';
    return value;
  };

  return (
    <Box>
      <Text fontSize="xs" color="gray.500" fontWeight="medium">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        {displayValue()}
      </Text>
    </Box>
  );
}

interface ValidatedInfoItemProps {
  label: string;
  cadastralValue: any;
  govValue: any;
  // eslint-disable-next-line no-unused-vars
  formatter?: (value: any) => string;
  isDivergent?: boolean; // Indicador de divergência vindo do backend
  divergenceMessage?: string; // Mensagem customizada de divergência
}

function ValidatedInfoItem({
  label,
  cadastralValue,
  govValue,
  formatter,
  isDivergent,
  divergenceMessage,
}: ValidatedInfoItemProps) {
  const formatValue = (val: any) => {
    if (val === null || val === undefined || val === '') return '-';
    return formatter ? formatter(val) : String(val);
  };

  const displayCadastral = formatValue(cadastralValue);
  const displayGov = formatValue(govValue);

  // Usa o indicador do backend se disponível, senão não mostra divergência
  // (o backend é a fonte de verdade agora)
  const hasMismatch = isDivergent === true;

  return (
    <Box>
      <Flex align="center" gap={1}>
        <Text fontSize="xs" color="gray.500" fontWeight="medium">
          {label}
        </Text>
        {hasMismatch && (
          <Tooltip
            label={
              <Box>
                <Text fontWeight="bold" mb={1}>
                  {divergenceMessage || 'Dados divergentes'}
                </Text>
                <Text>Cadastrado: {displayCadastral}</Text>
                <Text>Receita Federal: {displayGov}</Text>
              </Box>
            }
            bg="red.600"
            color="white"
            placement="top"
            hasArrow
          >
            <Box as="span" display="inline-flex" cursor="help">
              <Icon as={WarningCircle} color="red.500" weight="fill" boxSize={4} />
            </Box>
          </Tooltip>
        )}
      </Flex>
      <Text fontSize="sm" fontWeight="semibold" color={hasMismatch ? 'red.600' : 'gray.700'}>
        {displayCadastral}
      </Text>
    </Box>
  );
}

const TABS_CONFIG = [
  { key: 'cadastrais', label: 'Dados Cadastrais', icon: Calendar, color: 'blue' },
  { key: 'gov', label: 'Dados GOV', icon: IdentificationCard, color: 'green' },
  { key: 'equipe', label: 'Equipe Responsável', icon: Users, color: 'blue' },
  { key: 'tags', label: 'Tags', icon: Tag, color: 'blue' },
  { key: 'contatos', label: 'Contatos', icon: Phone, color: 'green' },
];

const TAB_SIMPLES = {
  key: 'simples',
  label: 'Simples Nacional',
  icon: Calculator,
  color: 'purple',
};

interface ContatoEmpresa {
  id: number;
  nome: string;
  telefone?: string;
  email?: string;
  cargo?: string;
  departamento?: string;
  tipo?: string;
  principal?: boolean;
}

function CompanyContactsTab({
  companyId,
  companyName,
}: {
  companyId?: number;
  companyName?: string;
}) {
  const [showModal, setShowModal] = React.useState(false);
  const [contatos, setContatos] = React.useState<ContatoEmpresa[]>([]);
  const [loading, setLoading] = React.useState(false);

  const fetchContatos = React.useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/empresas/${companyId}/contatos/`);
      const data = res.data?.data?.items || res.data?.data || res.data?.items || [];
      setContatos(Array.isArray(data) ? data : []);
    } catch {
      setContatos([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  React.useEffect(() => {
    fetchContatos();
  }, [fetchContatos]);

  const handleDeleteContato = async (contatoId: number) => {
    try {
      await api.delete(`/api/v1/empresas/${companyId}/contatos/${contatoId}/`);
      setContatos(prev => prev.filter(c => c.id !== contatoId));
    } catch {
      // silencioso
    }
  };

  return (
    <Flex direction="column" gap={4}>
      <Flex justify="space-between" align="center">
        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
          Contatos de {companyName || 'empresa'}
        </Text>
        <Button
          size="sm"
          colorScheme="green"
          variant="outline"
          borderRadius="lg"
          onClick={() => setShowModal(true)}
        >
          + Novo Contato
        </Button>
      </Flex>

      {loading && (
        <Box textAlign="center" py={6}>
          <Spinner size="sm" />
        </Box>
      )}

      {!loading && contatos.length === 0 && (
        <Box bg="gray.50" borderRadius="xl" p={4}>
          <Text fontSize="xs" color="gray.500" textAlign="center" py={6}>
            Nenhum contato cadastrado. Clique em &ldquo;+ Novo Contato&rdquo; para adicionar.
          </Text>
        </Box>
      )}

      {!loading && contatos.length > 0 && (
        <Box>
          {contatos.map(c => (
            <Flex
              key={c.id}
              align="center"
              justify="space-between"
              bg="gray.50"
              borderRadius="lg"
              px={4}
              py={3}
              mb={2}
            >
              <Box>
                <Flex align="center" gap={2}>
                  <Text fontSize="sm" fontWeight="semibold">
                    {c.nome}
                  </Text>
                  {c.principal && (
                    <Badge colorScheme="green" fontSize="2xs">
                      Principal
                    </Badge>
                  )}
                </Flex>
                <Flex gap={3} mt={1}>
                  {c.telefone && (
                    <Text fontSize="xs" color="gray.500">
                      {c.telefone}
                    </Text>
                  )}
                  {c.email && (
                    <Text fontSize="xs" color="gray.500">
                      {c.email}
                    </Text>
                  )}
                  {c.cargo && (
                    <Text fontSize="xs" color="gray.400">
                      {c.cargo}
                    </Text>
                  )}
                </Flex>
              </Box>
              <IconButton
                aria-label="Remover contato"
                icon={<Trash size={14} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteContato(c.id)}
              />
            </Flex>
          ))}
        </Box>
      )}

      <Text fontSize="xs" color="gray.400">
        Contatos cadastrados aqui ficam disponíveis na Central de Relacionamento automaticamente.
      </Text>

      <ContactFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        empresaId={companyId}
        onSave={() => {
          setShowModal(false);
          fetchContatos();
        }}
      />
    </Flex>
  );
}

export function CompanyDetailModal({
  isOpen,
  onClose,
  company,
  onEditCharacteristics,
  showNotesButton = false,
  showDashboardButton = false,
}: CompanyDetailModalProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { uploadEmpresaLogo, deleteEmpresaLogo, getCompany } = useCompanies();
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const {
    isOpen: isDashboardOpen,
    onOpen: onDashboardOpen,
    onClose: onDashboardClose,
  } = useDisclosure();

  // Buscar dados frescos da empresa (inclui logo_url presigned)
  const { data: freshCompanyData } = useQuery({
    queryKey: ['company-detail', { companyId: company?.id }],
    queryFn: getCompany,
    enabled: isOpen && !!company?.id,
  });

  useEffect(() => {
    // freshCompanyData = {data: empresa, meta: {}}
    const fresh = (freshCompanyData as any)?.data;
    setLogoUrl(fresh?.logo_url || company?.logo_url || null);
  }, [freshCompanyData, company?.id, company?.logo_url]);

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => uploadEmpresaLogo({ empresaId: company?.id || 0, file }),
    onSuccess: (res: any) => {
      toast({ title: 'Logo atualizada', status: 'success', duration: 2000 });
      setLogoUrl(res?.data?.logo_url || null);
      queryClient.invalidateQueries({ queryKey: ['company-detail', { companyId: company?.id }] });
    },
    onError: () => {
      toast({ title: 'Erro ao enviar logo', status: 'error', duration: 3000 });
    },
  });

  const deleteLogoMutation = useMutation({
    mutationFn: () => deleteEmpresaLogo(company?.id || 0),
    onSuccess: () => {
      toast({ title: 'Logo removida', status: 'success', duration: 2000 });
      setLogoUrl(null);
    },
    onError: () => {
      toast({ title: 'Erro ao remover logo', status: 'error', duration: 3000 });
    },
  });

  // aba Simples: some sem permissão na tela do Simples; desabilitada fora do Simples
  const { data: simples, isSuccess: temAbaSimples } = useParametroSn(
    isOpen ? company?.id : undefined,
  );
  const abas = temAbaSimples ? [...TABS_CONFIG, TAB_SIMPLES] : TABS_CONFIG;

  if (!company) return null;

  // Datas para o modal de anotações
  const today = new Date();
  const dateEnd = today.toISOString().split('T')[0];
  const dateStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];

  // Valores atribuídos à empresa (via "editar características")
  const companyFieldValues = company.campos_valores || [];

  const handleOpenDossie = () => {
    if (company?.external_id) {
      onClose();
      navigate(`/dossie-empresa/${company.external_id}`, {
        state: { company },
      });
    }
  };

  const formatDate = (date: any) => {
    if (!date) return '-';
    try {
      return new Date(date).toLocaleDateString('pt-BR');
    } catch {
      return date;
    }
  };

  const isAtiva = () => {
    const { ativa } = company;
    if (ativa === true || ativa === 'Sim' || ativa === 1 || ativa === 'Ativa') return true;
    return false;
  };

  const getStatusColor = () => {
    return isAtiva() ? 'green' : 'red';
  };

  // Conta divergências usando os indicadores do backend
  const getMismatchCount = () => {
    return [
      company.div_cnpj_invalido,
      company.div_razao_social,
      company.div_cnpj,
      company.div_cidade,
      company.div_uf,
    ].filter(Boolean).length;
  };

  const mismatchCount = getMismatchCount();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent maxH="90vh" overflow="hidden">
        <Flex>
          {/* Conteúdo principal */}
          <Box flex="1" overflow="auto" maxH="90vh">
            <ModalHeader pb={2}>
              <Flex align="flex-start" gap={3} justify="space-between">
                <Flex align="center" gap={3} flex={1}>
                  <Icon as={Buildings} boxSize={6} color="blue.500" />
                  <Box>
                    <Flex align="center" gap={2} flexWrap="wrap">
                      <Text fontSize="lg" fontWeight="bold" color="gray.800">
                        {company.razao_social || company.nome}
                      </Text>
                      <Badge colorScheme={getStatusColor()} fontSize="xs">
                        {isAtiva() ? 'Ativa' : 'Inativa'}
                      </Badge>
                      {mismatchCount > 0 && (
                        <Tooltip
                          label={`${mismatchCount} divergência${mismatchCount > 1 ? 's' : ''} encontrada${mismatchCount > 1 ? 's' : ''} entre os dados cadastrais e a Receita Federal`}
                          bg="red.600"
                          color="white"
                          placement="top"
                          hasArrow
                        >
                          <Badge
                            colorScheme="red"
                            fontSize="xs"
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            <Icon as={WarningCircle} weight="fill" boxSize={3} />
                            {mismatchCount} divergência{mismatchCount > 1 ? 's' : ''}
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                    <Flex gap={4} mt={1} flexWrap="wrap" align="center">
                      <Text fontSize="sm" color="gray.500">
                        <strong>ID:</strong> {company.external_id}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        <strong>{getDocumentType(company.cnpj)}:</strong>{' '}
                        {formatCPFOrCNPJ(company.cnpj)}
                      </Text>
                      {company.grupo_de_empresas && (
                        <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5}>
                          <Flex align="center" gap={1}>
                            <Icon as={Tag} boxSize={3} />
                            {company.grupo_de_empresas}
                          </Flex>
                        </Badge>
                      )}
                    </Flex>
                  </Box>
                </Flex>
                <Button
                  leftIcon={<FolderOpen size={16} />}
                  size="sm"
                  variant="outline"
                  color="orange.500"
                  borderColor="orange.500"
                  onClick={handleOpenDossie}
                  isDisabled={!company?.external_id}
                  flexShrink={0}
                  _hover={{
                    bg: 'orange.50',
                    borderColor: 'orange.600',
                    color: 'orange.600',
                  }}
                >
                  Ver Dossiê
                </Button>
              </Flex>
            </ModalHeader>

            <ModalBody pt={0} pb={6}>
              <Tabs variant="unstyled" defaultIndex={0}>
                <TabList
                  flexWrap="wrap"
                  bg="gray.50"
                  px={2}
                  pt={2}
                  gap={1}
                  borderBottom="none"
                  borderTopRadius="lg"
                >
                  {abas.map(tab => (
                    <Tab
                      key={tab.key}
                      isDisabled={tab.key === 'simples' && !simples?.is_simples_nacional}
                      title={
                        tab.key === 'simples' && !simples?.is_simples_nacional
                          ? 'A empresa não está no Simples Nacional'
                          : undefined
                      }
                      fontWeight="semibold"
                      fontSize="sm"
                      px={4}
                      py={2}
                      borderTopRadius="lg"
                      borderBottomRadius="none"
                      border="1px solid"
                      borderBottom="none"
                      mb="-1px"
                      transition="all 0.2s"
                      bg="white"
                      color="gray.600"
                      borderColor="gray.200"
                      _selected={{
                        bg: `${tab.color}.50`,
                        color: `${tab.color}.700`,
                        borderColor: `${tab.color}.200`,
                        boxShadow: 'sm',
                      }}
                      _hover={{ bg: 'gray.100' }}
                    >
                      <Icon as={tab.icon} mr={2} />
                      {tab.label}
                    </Tab>
                  ))}
                </TabList>

                <TabPanels>
                  {/* Dados Cadastrais */}
                  <TabPanel
                    p={4}
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderTop="none"
                    borderBottomRadius="lg"
                    minH="300px"
                  >
                    <Flex direction="column" gap={5}>
                      {/* Logo da empresa */}
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color="blue.600"
                          mb={3}
                          textTransform="uppercase"
                        >
                          Logo da Empresa
                        </Text>
                        <Flex align="center" gap={3}>
                          {logoUrl ? (
                            <Image
                              src={logoUrl}
                              alt="Logo"
                              maxH="48px"
                              maxW="120px"
                              objectFit="contain"
                              borderRadius="md"
                              border="1px solid"
                              borderColor="gray.200"
                              p={1}
                              bg="white"
                            />
                          ) : (
                            <Flex
                              w="48px"
                              h="48px"
                              bg="gray.100"
                              borderRadius="md"
                              align="center"
                              justify="center"
                              border="1px dashed"
                              borderColor="gray.300"
                            >
                              <Icon as={Camera} boxSize={5} color="gray.400" />
                            </Flex>
                          )}
                          <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/webp"
                            style={{ display: 'none' }}
                            onChange={e => {
                              const file = e.target.files?.[0];
                              if (file) uploadLogoMutation.mutate(file);
                              e.target.value = '';
                            }}
                          />
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="blue"
                            leftIcon={<Camera size={12} />}
                            onClick={() => logoInputRef.current?.click()}
                            isLoading={uploadLogoMutation.isPending}
                          >
                            {logoUrl ? 'Alterar' : 'Enviar logo'}
                          </Button>
                          {logoUrl && (
                            <IconButton
                              aria-label="Remover logo"
                              icon={<Trash size={14} />}
                              size="xs"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => deleteLogoMutation.mutate()}
                              isLoading={deleteLogoMutation.isPending}
                            />
                          )}
                        </Flex>
                      </Box>

                      {/* Identificação */}
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color="blue.600"
                          mb={3}
                          textTransform="uppercase"
                        >
                          Identificação
                        </Text>
                        <Grid templateColumns="2fr 1fr 1fr" gap={4}>
                          <ValidatedInfoItem
                            label="Razão Social"
                            cadastralValue={company.razao_social || company.nome}
                            govValue={company.gov_razao_social || company.empresa_gov?.razao_social}
                            isDivergent={company.div_razao_social}
                            divergenceMessage="Razão Social divergente"
                          />
                          <ValidatedInfoItem
                            label={getDocumentType(company.cnpj)}
                            cadastralValue={company.cnpj}
                            govValue={company.gov_cnpj || company.empresa_gov?.cnpj}
                            formatter={formatCPFOrCNPJ}
                            isDivergent={company.div_cnpj_invalido || company.div_cnpj}
                            divergenceMessage={
                              company.div_cnpj_invalido
                                ? `${getDocumentType(company.cnpj)} inválido ou ausente`
                                : `${getDocumentType(company.cnpj)} divergente`
                            }
                          />
                          <InfoItem
                            label="Regime Tributário"
                            value={company.regime_tributario_atualizado}
                          />
                        </Grid>
                      </Box>

                      {/* Localização */}
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color="blue.600"
                          mb={3}
                          textTransform="uppercase"
                        >
                          Localização
                        </Text>
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                          <ValidatedInfoItem
                            label="Cidade"
                            cadastralValue={company.cidade}
                            govValue={company.gov_cidade || company.empresa_gov?.cidade}
                            isDivergent={company.div_cidade}
                            divergenceMessage="Cidade divergente"
                          />
                          <ValidatedInfoItem
                            label="Estado"
                            cadastralValue={company.uf}
                            govValue={company.gov_uf || company.empresa_gov?.estado}
                            isDivergent={company.div_uf}
                            divergenceMessage="Estado divergente"
                          />
                        </Grid>
                      </Box>

                      {/* Datas e Status */}
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="bold"
                          color="blue.600"
                          mb={3}
                          textTransform="uppercase"
                        >
                          Datas e Status
                        </Text>
                        <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                          <InfoItem
                            label="Cliente Desde"
                            value={formatDate(company.dt_cli_desde)}
                          />
                          <InfoItem label="Cliente Até" value={formatDate(company.dt_cli_ate)} />
                          <InfoItem
                            label="Paga por Quotas"
                            value={company.paga_por_quotas ? 'Sim' : 'Não'}
                          />
                          <InfoItem
                            label="Data de Cadastro"
                            value={formatDate(company.dt_cadastro)}
                          />
                          <InfoItem label="Data Criação" value={formatDate(company.dt_criacao)} />
                          <InfoItem
                            label="Última Atualização"
                            value={formatDate(company.dt_atualizacao)}
                          />
                        </Grid>
                      </Box>
                    </Flex>
                  </TabPanel>

                  {/* Dados GOV */}
                  <TabPanel
                    p={4}
                    bg="green.50"
                    border="1px solid"
                    borderColor="green.200"
                    borderTop="none"
                    borderBottomRadius="lg"
                    minH="300px"
                  >
                    {company.empresa_gov ? (
                      <Flex direction="column" gap={5}>
                        {/* Identificação */}
                        <Box>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="green.600"
                            mb={3}
                            textTransform="uppercase"
                          >
                            Identificação
                          </Text>
                          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                            <InfoItem
                              label="Razão Social"
                              value={company.empresa_gov.razao_social}
                            />
                            <InfoItem
                              label="Nome Fantasia"
                              value={company.empresa_gov.nome_fantasia}
                            />
                            <InfoItem
                              label={getDocumentType(company.empresa_gov.cnpj)}
                              value={
                                company.empresa_gov.cnpj
                                  ? formatCPFOrCNPJ(company.empresa_gov.cnpj)
                                  : '-'
                              }
                            />
                            <InfoItem
                              label="Natureza Jurídica"
                              value={company.empresa_gov.natureza_juridica}
                            />
                            <InfoItem label="Porte" value={company.empresa_gov.porte} />
                            <InfoItem label="Matriz" value={company.empresa_gov.matriz} />
                          </Grid>
                        </Box>

                        {/* Status */}
                        <Box>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="green.600"
                            mb={3}
                            textTransform="uppercase"
                          >
                            Status
                          </Text>
                          <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                            <Box>
                              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                                Situação
                              </Text>
                              <Badge
                                colorScheme={
                                  company.empresa_gov.status === 'Ativa' ? 'green' : 'red'
                                }
                                fontSize="sm"
                              >
                                {company.empresa_gov.status || '-'}
                              </Badge>
                            </Box>
                            <InfoItem
                              label="Data Situação"
                              value={formatDate(company.empresa_gov.data_status)}
                            />
                            <InfoItem
                              label="Atualizado em"
                              value={formatDate(company.empresa_gov.atualizado_em)}
                            />
                          </Grid>
                        </Box>

                        {/* Endereço */}
                        <Box>
                          <Flex align="center" gap={2} mb={3}>
                            <Icon as={MapPin} color="green.600" />
                            <Text
                              fontSize="xs"
                              fontWeight="bold"
                              color="green.600"
                              textTransform="uppercase"
                            >
                              Endereço
                            </Text>
                          </Flex>
                          <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                            <Box gridColumn="span 2">
                              <InfoItem label="Logradouro" value={company.empresa_gov.logradouro} />
                            </Box>
                            <InfoItem label="Número" value={company.empresa_gov.numero} />
                            <InfoItem label="Complemento" value={company.empresa_gov.complemento} />
                            <InfoItem label="Bairro" value={company.empresa_gov.bairro} />
                            <InfoItem label="Cidade" value={company.empresa_gov.cidade} />
                            <InfoItem label="Estado" value={company.empresa_gov.estado} />
                            <InfoItem label="CEP" value={company.empresa_gov.cep} />
                          </Grid>
                        </Box>

                        {/* CNAE */}
                        <Box>
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="green.600"
                            mb={3}
                            textTransform="uppercase"
                          >
                            Atividade Econômica (CNAE)
                          </Text>
                          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                            <InfoItem
                              label="Código CNAE Principal"
                              value={company.empresa_gov.cnae_principal_cod}
                            />
                            <InfoItem
                              label="Descrição CNAE Principal"
                              value={company.empresa_gov.cnae_principal_desc}
                            />
                            {company.empresa_gov.cnae_secundario && (
                              <Box gridColumn="span 2">
                                <InfoItem
                                  label="CNAEs Secundários"
                                  value={company.empresa_gov.cnae_secundario}
                                />
                              </Box>
                            )}
                          </Grid>
                        </Box>
                      </Flex>
                    ) : (
                      <Flex align="center" justify="center" h="200px" direction="column" gap={3}>
                        <Icon as={WarningCircle} boxSize={10} color="gray.400" />
                        <Text color="gray.500" fontSize="sm" textAlign="center">
                          Dados da Receita Federal não disponíveis para esta empresa.
                        </Text>
                      </Flex>
                    )}
                  </TabPanel>

                  {/* Equipe Responsável */}
                  <TabPanel
                    p={4}
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderTop="none"
                    borderBottomRadius="lg"
                    minH="300px"
                  >
                    <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Comercial
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.comercial || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_comercial || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Contábil
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.contabil || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_contabil || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Financeiro
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.financeiro || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_financeiro || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Fiscal
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.fiscal || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_fiscal || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Contas a Pagar
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.contas_a_pagar || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_contas_a_pagar || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Encerramento Anual
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.encerramento_anual || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_encerramento_anual || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Imposto de Renda
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.imposto_de_renda || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_imposto_de_renda || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Integração Contábil
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.integracao_contabil || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_integracao_contabil || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Integração Fiscal
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.integracao_fiscal || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_integracao_fiscal || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Pessoal
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.pessoal || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_pessoal || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Relacionamento
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.relacionamento_com_cliente || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_relacionamento_com_cliente || ''}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500" fontWeight="medium">
                          Societário
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                          {company.societario || '-'}
                        </Text>
                        <Text fontSize="xs" color="blue.600">
                          {company.resp_societario || ''}
                        </Text>
                      </Box>
                    </Grid>
                  </TabPanel>

                  {/* Tags */}
                  <TabPanel
                    p={4}
                    bg="blue.50"
                    border="1px solid"
                    borderColor="blue.200"
                    borderTop="none"
                    borderBottomRadius="lg"
                    minH="300px"
                  >
                    <Flex direction="column" gap={6}>
                      {/* Seção: Tags */}
                      <Box>
                        <Flex align="center" gap={2} mb={3}>
                          <Icon as={Tag} color="purple.500" boxSize={4} />
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="purple.600"
                            textTransform="uppercase"
                          >
                            Tags
                          </Text>
                        </Flex>
                        {company.tags ? (
                          <Flex wrap="wrap" gap={2}>
                            {company.tags
                              .split(',')
                              .map(tag => tag.trim())
                              .filter(Boolean)
                              .map(tag => (
                                <Badge
                                  key={tag}
                                  colorScheme="purple"
                                  variant="subtle"
                                  px={3}
                                  py={1.5}
                                  borderRadius="md"
                                  fontSize="sm"
                                  fontWeight="medium"
                                >
                                  {tag}
                                </Badge>
                              ))}
                          </Flex>
                        ) : (
                          <Text color="gray.500" fontSize="sm">
                            Nenhuma tag cadastrada.
                          </Text>
                        )}
                      </Box>

                      {/* Seção: Campos (com valores atribuídos) */}
                      <Box>
                        <Flex align="center" gap={2} mb={3}>
                          <Icon as={Gear} color="blue.500" boxSize={4} />
                          <Text
                            fontSize="xs"
                            fontWeight="bold"
                            color="blue.600"
                            textTransform="uppercase"
                          >
                            Campos
                          </Text>
                        </Flex>
                        {(() => {
                          // Agrupa valores por campo
                          const valuesByField: Record<string, string[]> = {};
                          companyFieldValues.forEach(fv => {
                            if (!valuesByField[fv.campo]) {
                              valuesByField[fv.campo] = [];
                            }
                            valuesByField[fv.campo].push(fv.value);
                          });

                          const fieldNames = Object.keys(valuesByField);

                          if (fieldNames.length === 0) {
                            return (
                              <Text color="gray.500" fontSize="sm">
                                Nenhum campo atribuído a esta empresa.
                              </Text>
                            );
                          }

                          return (
                            <Flex direction="column" gap={4}>
                              {fieldNames.map(fieldName => (
                                <Box key={fieldName}>
                                  <Text fontSize="xs" fontWeight="semibold" color="blue.600" mb={2}>
                                    {fieldName.replace(/_/g, ' ')}
                                  </Text>
                                  <Flex wrap="wrap" gap={2}>
                                    {valuesByField[fieldName].map(value => (
                                      <Badge
                                        key={`${fieldName}-${value}`}
                                        colorScheme="orange"
                                        variant="subtle"
                                        px={3}
                                        py={1.5}
                                        borderRadius="md"
                                        fontSize="sm"
                                        fontWeight="medium"
                                      >
                                        {value}
                                      </Badge>
                                    ))}
                                  </Flex>
                                </Box>
                              ))}
                            </Flex>
                          );
                        })()}
                      </Box>
                    </Flex>
                  </TabPanel>

                  {/* Contatos */}
                  <TabPanel px={0}>
                    <CompanyContactsTab companyId={company?.id} companyName={company?.nome} />
                  </TabPanel>

                  {/* Simples Nacional (parâmetros da apuração própria) */}
                  {temAbaSimples && (
                    <TabPanel
                      p={4}
                      bg="purple.50"
                      border="1px solid"
                      borderColor="purple.200"
                      borderTop="none"
                      borderBottomRadius="lg"
                      minH="300px"
                    >
                      {simples?.is_simples_nacional && (
                        <SimplesNacionalTab empresaId={company.id} />
                      )}
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            </ModalBody>
          </Box>

          {/* Coluna de ações (direita) */}
          <Flex
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-start"
            gap={1}
            py={2}
            px={1}
            bg="gray.50"
            borderLeft="1px"
            borderColor="gray.200"
            minW="40px"
          >
            {/* Botão de fechar */}
            <Tooltip label="Fechar" placement="left">
              <IconButton
                aria-label="Fechar"
                icon={<Icon as={X} weight="bold" boxSize={4} />}
                size="xs"
                variant="ghost"
                borderRadius="md"
                onClick={onClose}
                color="gray.500"
                _hover={{
                  bg: 'gray.200',
                  color: 'gray.700',
                }}
              />
            </Tooltip>

            <Box borderTop="1px" borderColor="gray.200" my={1} />

            {/* Botão de anotações */}
            {showNotesButton && (
              <ViewNotaModal
                id={company.id}
                dateStart={dateStart}
                dateEnd={dateEnd}
                defaultTab="empresas"
                companyName={company.razao_social || company.nome}
              >
                <Box as="span">
                  <Tooltip label="Anotações" placement="left">
                    <IconButton
                      aria-label="Anotações"
                      icon={<Icon as={NotePencil} weight="fill" boxSize={4} />}
                      size="xs"
                      variant="ghost"
                      borderRadius="md"
                      color="purple.500"
                      _hover={{
                        bg: 'purple.50',
                        color: 'purple.600',
                      }}
                    />
                  </Tooltip>
                </Box>
              </ViewNotaModal>
            )}

            {/* Botão de editar características */}
            {onEditCharacteristics && (
              <Tooltip label="Editar características" placement="left">
                <IconButton
                  aria-label="Editar características"
                  icon={<Icon as={PencilSimple} weight="fill" boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  borderRadius="md"
                  color="blue.500"
                  onClick={() => onEditCharacteristics(company)}
                  _hover={{
                    bg: 'blue.50',
                    color: 'blue.600',
                  }}
                />
              </Tooltip>
            )}

            {/* Botão de Dashboard */}
            {showDashboardButton && (
              <Tooltip label="Dashboard" placement="left">
                <IconButton
                  aria-label="Dashboard"
                  icon={<Icon as={ChartBar} weight="fill" boxSize={4} />}
                  size="xs"
                  variant="ghost"
                  borderRadius="md"
                  color="teal.500"
                  onClick={onDashboardOpen}
                  _hover={{
                    bg: 'teal.50',
                    color: 'teal.600',
                  }}
                />
              </Tooltip>
            )}

            {/* Botão de Dossiê */}
            <Tooltip label="Ver Dossiê" placement="left">
              <IconButton
                aria-label="Ver Dossiê"
                icon={<Icon as={FolderOpen} weight="fill" boxSize={4} />}
                size="xs"
                variant="ghost"
                borderRadius="md"
                color="orange.500"
                onClick={handleOpenDossie}
                isDisabled={!company?.external_id}
                _hover={{
                  bg: 'orange.50',
                  color: 'orange.600',
                }}
              />
            </Tooltip>
          </Flex>
        </Flex>

        {/* Modal de Dashboard da Empresa */}
        <EmpresaDetailModal isOpen={isDashboardOpen} onClose={onDashboardClose} empresa={company} />
      </ModalContent>
    </Modal>
  );
}
